<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <div>
        <h2 class="text-2xl font-bold text-gray-900">Google Business Profile</h2>
        <p class="text-sm text-gray-500 mt-1">Sync your hours, services, and menus with Google</p>
      </div>
      <div v-if="isConnected" class="flex items-center space-x-2">
        <span class="flex items-center text-green-600 text-sm">
          <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
          Connected
        </span>
        <button
          @click="disconnect"
          class="text-red-600 hover:text-red-800 text-sm underline"
        >
          Disconnect
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      <p class="mt-4 text-gray-600">{{ loadingMessage }}</p>
    </div>

    <!-- Not Connected State -->
    <div v-else-if="!isConnected" class="bg-white border rounded-lg p-8 text-center">
      <div class="max-w-md mx-auto">
        <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">Connect to Google Business Profile</h3>
        <p class="text-gray-600 mb-6">
          Link your Google Business Profile to sync your operating hours, tour services, and menu items automatically.
        </p>
        <button
          @click="connectGoogle"
          :disabled="connecting"
          class="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {{ connecting ? 'Connecting...' : 'Connect with Google' }}
        </button>
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="space-y-6">
      <!-- Location Selector -->
      <div v-if="!selectedLocation" class="bg-white border rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Select Your Business Location</h3>

        <div v-if="loadingLocations" class="text-center py-4">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>

        <div v-else-if="accounts.length === 0" class="text-gray-500 text-center py-4">
          No Google Business accounts found. Make sure your Google account has access to a Business Profile.
        </div>

        <div v-else class="space-y-4">
          <!-- Account Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Account</label>
            <select
              v-model="selectedAccountId"
              @change="loadLocations"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select an account...</option>
              <option v-for="account in accounts" :key="account.name" :value="account.name">
                {{ account.accountName || account.name }}
              </option>
            </select>
          </div>

          <!-- Location Selection -->
          <div v-if="selectedAccountId && locations.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              v-model="selectedLocationName"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a location...</option>
              <option v-for="location in locations" :key="location.name" :value="location.name">
                {{ location.title || location.locationName }}
              </option>
            </select>
          </div>

          <button
            v-if="selectedLocationName"
            @click="saveLocationSelection"
            class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Use This Location
          </button>
        </div>
      </div>

      <!-- Main Management Interface -->
      <div v-else class="space-y-6">
        <!-- Selected Location Header -->
        <div class="bg-green-50 border border-green-200 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p class="text-sm text-green-600">Managing location:</p>
            <p class="font-semibold text-green-800">{{ selectedLocationTitle }}</p>
          </div>
          <button
            @click="changeLocation"
            class="text-green-600 hover:text-green-800 text-sm underline"
          >
            Change Location
          </button>
        </div>

        <!-- Sync Sections -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Hours Sync -->
          <div class="bg-white border rounded-lg p-6">
            <div class="flex items-start justify-between mb-4">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Operating Hours</h3>
                <p class="text-sm text-gray-500">Sync regular & special hours to Google</p>
              </div>
              <span class="text-2xl">🕐</span>
            </div>

            <div class="space-y-3 mb-4">
              <label class="flex items-center space-x-2">
                <input
                  v-model="syncOptions.regularHours"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span class="text-sm text-gray-700">Regular business hours</span>
              </label>
              <label class="flex items-center space-x-2">
                <input
                  v-model="syncOptions.specialHours"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span class="text-sm text-gray-700">Special hours (holidays & Sunday openings)</span>
              </label>
            </div>

            <button
              @click="syncHours"
              :disabled="syncing.hours || (!syncOptions.regularHours && !syncOptions.specialHours)"
              class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              <span v-if="syncing.hours" class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              {{ syncing.hours ? 'Syncing...' : 'Sync Hours to Google' }}
            </button>
          </div>

          <!-- Services Sync -->
          <div class="bg-white border rounded-lg p-6">
            <div class="flex items-start justify-between mb-4">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Tour Services</h3>
                <p class="text-sm text-gray-500">Update your tour offerings on Google</p>
              </div>
              <span class="text-2xl">🎫</span>
            </div>

            <div class="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
              <p class="font-medium text-gray-700 mb-2">Services to sync:</p>
              <ul class="space-y-1 text-gray-600">
                <li>• Distillery Experience Tour - 4,000 VT</li>
                <li>• Cellar Door Tasting - 2,500 VT</li>
              </ul>
            </div>

            <button
              @click="syncServices"
              :disabled="syncing.services"
              class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              <span v-if="syncing.services" class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              {{ syncing.services ? 'Syncing...' : 'Sync Services to Google' }}
            </button>
          </div>

          <!-- Food Menu Sync -->
          <div class="bg-white border rounded-lg p-6 lg:col-span-2">
            <div class="flex items-start justify-between mb-4">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Food & Drink Menu</h3>
                <p class="text-sm text-gray-500">Manage your tasting room menu on Google</p>
              </div>
              <span class="text-2xl">🍹</span>
            </div>

            <!-- Menu Editor -->
            <div class="space-y-4 mb-4">
              <div v-for="(section, sectionIndex) in menuSections" :key="sectionIndex" class="border rounded-lg p-4">
                <div class="flex justify-between items-center mb-3">
                  <input
                    v-model="section.name"
                    placeholder="Section Name (e.g., Rum Selection)"
                    class="font-medium text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    @click="removeMenuSection(sectionIndex)"
                    class="text-red-500 hover:text-red-700"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <div class="space-y-2">
                  <div v-for="(item, itemIndex) in section.items" :key="itemIndex" class="bg-gray-50 p-3 rounded space-y-2">
                    <div class="flex items-center space-x-2">
                      <input
                        v-model="item.name"
                        placeholder="Item name"
                        class="flex-grow px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                      <input
                        v-model="item.price"
                        type="number"
                        placeholder="Price"
                        class="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                      <span class="text-sm text-gray-500">VT</span>
                      <button
                        @click="removeMenuItem(sectionIndex, itemIndex)"
                        class="text-red-500 hover:text-red-700"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <input
                      v-model="item.description"
                      placeholder="Description (optional)"
                      class="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <button
                    @click="addMenuItem(sectionIndex)"
                    class="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Item
                  </button>
                </div>
              </div>

              <button
                @click="addMenuSection"
                class="w-full border-2 border-dashed border-gray-300 rounded-lg p-3 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
              >
                + Add Menu Section
              </button>
            </div>

            <div class="flex space-x-3">
              <button
                @click="saveMenuOnly"
                :disabled="savingMenu"
                class="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                <span v-if="savingMenu" class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {{ savingMenu ? 'Saving...' : 'Save Menu' }}
              </button>
              <button
                @click="syncFoodMenus"
                :disabled="syncing.menus || menuSections.length === 0"
                class="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                <span v-if="syncing.menus" class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {{ syncing.menus ? 'Syncing...' : 'Save & Sync to Google' }}
              </button>
            </div>
            <p class="text-xs text-gray-500 mt-2 text-center">Note: Menu may not appear on Google if your primary category is not food-related</p>
          </div>
        </div>

        <!-- Sync All Button -->
        <div class="bg-gray-50 rounded-lg p-6 text-center">
          <button
            @click="syncAll"
            :disabled="syncing.all"
            class="inline-flex items-center px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <span v-if="syncing.all" class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></span>
            <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ syncing.all ? 'Syncing Everything...' : 'Sync Everything to Google' }}
          </button>
          <p class="text-sm text-gray-500 mt-2">Push all hours, services, and menu data to your Google Business Profile</p>
        </div>
      </div>
    </div>

    <!-- Error/Success Messages -->
    <div v-if="message" :class="[
      'fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-all',
      message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    ]">
      {{ message.text }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useAuth } from '../../composables/useAuth'
import axios from 'axios'

const { getAuthAxios } = useAuth()

// State
const loading = ref(true)
const loadingMessage = ref('Checking connection status...')
const connecting = ref(false)
const isConnected = ref(false)
const loadingLocations = ref(false)

// Account/Location selection
const accounts = ref([])
const locations = ref([])
const selectedAccountId = ref('')
const selectedLocationName = ref('')
const selectedLocationTitle = ref('')
const selectedLocation = ref(null)

// Sync options
const syncOptions = ref({
  regularHours: true,
  specialHours: true
})

const syncing = ref({
  hours: false,
  services: false,
  menus: false,
  all: false
})

const savingMenu = ref(false)

// Menu editor
const menuSections = ref([
  {
    name: 'Rum Selection',
    items: [
      { name: 'White Rum', price: 500, description: '' },
      { name: 'Gold Rum', price: 600, description: '' },
      { name: 'Spiced Rum', price: 600, description: '' }
    ]
  }
])

// Messages
const message = ref(null)

const showMessage = (text, type = 'success') => {
  message.value = { text, type }
  setTimeout(() => { message.value = null }, 4000)
}

// Check connection status
const checkConnectionStatus = async () => {
  try {
    const response = await axios.get('/api/google-business-auth?action=status')
    isConnected.value = response.data.connected

    if (isConnected.value) {
      await loadSettings()
      // Only load accounts if we don't have a saved location yet
      if (!selectedLocationName.value) {
        await loadAccounts()
      }
    }
  } catch (error) {
    console.error('Error checking connection:', error)
  } finally {
    loading.value = false
  }
}

// Load saved settings
const loadSettings = async () => {
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get('/api/google-business?action=settings')

    if (response.data.settings) {
      selectedAccountId.value = response.data.settings.selectedAccountId || ''
      selectedLocationName.value = response.data.settings.selectedLocationName || ''
      selectedLocationTitle.value = response.data.settings.selectedLocationTitle || ''

      if (selectedLocationName.value) {
        selectedLocation.value = { name: selectedLocationName.value }
      }
    }

    // Also load saved menu data
    await loadMenuData()
  } catch (error) {
    console.error('Error loading settings:', error)
  }
}

// Load saved menu data
const loadMenuData = async () => {
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get('/api/google-business?action=menu-data')

    if (response.data.menuSections && response.data.menuSections.length > 0) {
      menuSections.value = response.data.menuSections
    }
  } catch (error) {
    console.error('Error loading menu data:', error)
  }
}

// Save menu data locally
const saveMenuData = async () => {
  try {
    const authAxios = getAuthAxios()
    await authAxios.post('/api/google-business?action=menu-data', {
      menuSections: menuSections.value
    })
  } catch (error) {
    console.error('Error saving menu data:', error)
  }
}

// Save menu only (without syncing to Google)
const saveMenuOnly = async () => {
  savingMenu.value = true
  try {
    await saveMenuData()
    showMessage('Menu saved successfully!')
  } catch (error) {
    console.error('Error saving menu:', error)
    showMessage('Failed to save menu', 'error')
  } finally {
    savingMenu.value = false
  }
}

// Connect to Google
const connectGoogle = async () => {
  connecting.value = true
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get('/api/google-business-auth?action=auth-url')

    if (response.data.authUrl) {
      // Open Google OAuth in a popup
      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2

      const popup = window.open(
        response.data.authUrl,
        'google-auth',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      // Poll for popup close
      const pollTimer = setInterval(async () => {
        if (popup.closed) {
          clearInterval(pollTimer)
          connecting.value = false
          await checkConnectionStatus()
        }
      }, 500)
    }
  } catch (error) {
    console.error('Error connecting:', error)
    showMessage('Failed to connect to Google', 'error')
    connecting.value = false
  }
}

// Disconnect
const disconnect = async () => {
  if (!confirm('Are you sure you want to disconnect from Google Business Profile?')) return

  try {
    const authAxios = getAuthAxios()
    await authAxios.delete('/api/google-business-auth')
    isConnected.value = false
    selectedLocation.value = null
    showMessage('Disconnected from Google Business Profile')
  } catch (error) {
    console.error('Error disconnecting:', error)
    showMessage('Failed to disconnect', 'error')
  }
}

// Load accounts
const loadAccounts = async () => {
  loadingLocations.value = true
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get('/api/google-business?action=accounts')
    accounts.value = response.data.accounts || []

    // If we have a saved account, load its locations
    if (selectedAccountId.value) {
      await loadLocations()
    }
  } catch (error) {
    console.error('Error loading accounts:', error)
    const errorMsg = error.response?.data?.error || 'Failed to load Google Business accounts'
    if (errorMsg.includes('Quota exceeded') || errorMsg.includes('rate')) {
      showMessage('Google API rate limit reached. Please wait a minute and try again.', 'error')
    } else {
      showMessage(errorMsg, 'error')
    }
  } finally {
    loadingLocations.value = false
  }
}

// Load locations for selected account
const loadLocations = async () => {
  if (!selectedAccountId.value) {
    locations.value = []
    return
  }

  loadingLocations.value = true
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get(`/api/google-business?action=locations&accountId=${encodeURIComponent(selectedAccountId.value)}`)
    locations.value = response.data.locations || []
  } catch (error) {
    console.error('Error loading locations:', error)
    showMessage('Failed to load locations', 'error')
  } finally {
    loadingLocations.value = false
  }
}

// Save location selection
const saveLocationSelection = async () => {
  const location = locations.value.find(l => l.name === selectedLocationName.value)
  if (!location) return

  try {
    const authAxios = getAuthAxios()
    await authAxios.post('/api/google-business?action=settings', {
      selectedAccountId: selectedAccountId.value,
      selectedLocationName: selectedLocationName.value,
      selectedLocationTitle: location.title || location.locationName
    })

    selectedLocationTitle.value = location.title || location.locationName
    selectedLocation.value = location
    showMessage('Location saved successfully')
  } catch (error) {
    console.error('Error saving location:', error)
    showMessage('Failed to save location', 'error')
  }
}

// Change location
const changeLocation = () => {
  selectedLocation.value = null
}

// Sync hours
const syncHours = async () => {
  syncing.value.hours = true
  try {
    const authAxios = getAuthAxios()

    // Build regular hours (Mon-Fri 8-5, Sat 12-6, closed Sunday)
    const regularHours = {
      periods: [
        { openDay: 'MONDAY', openTime: '08:00', closeDay: 'MONDAY', closeTime: '17:00' },
        { openDay: 'TUESDAY', openTime: '08:00', closeDay: 'TUESDAY', closeTime: '17:00' },
        { openDay: 'WEDNESDAY', openTime: '08:00', closeDay: 'WEDNESDAY', closeTime: '17:00' },
        { openDay: 'THURSDAY', openTime: '08:00', closeDay: 'THURSDAY', closeTime: '17:00' },
        { openDay: 'FRIDAY', openTime: '08:00', closeDay: 'FRIDAY', closeTime: '17:00' },
        { openDay: 'SATURDAY', openTime: '12:00', closeDay: 'SATURDAY', closeTime: '18:00' }
      ]
    }

    // Build special hours from holidays
    const holidaysResponse = await axios.get('/api/admin-holidays')
    const holidays = holidaysResponse.data.holidays || []

    const specialHours = {
      specialHourPeriods: holidays
        .filter(h => h.actionType === 'block' || h.actionType === 'sunday_open')
        .map(h => {
          if (h.actionType === 'sunday_open') {
            // Parse opening time
            const openTime = h.openingTime?.replace(' AM', '').replace(' PM', '').padStart(5, '0') || '10:00'
            return {
              startDate: { year: parseInt(h.date.split('-')[0]), month: parseInt(h.date.split('-')[1]), day: parseInt(h.date.split('-')[2]) },
              openTime: openTime,
              closeTime: '18:00',
              isClosed: false
            }
          } else {
            return {
              startDate: { year: parseInt(h.date.split('-')[0]), month: parseInt(h.date.split('-')[1]), day: parseInt(h.date.split('-')[2]) },
              isClosed: true
            }
          }
        })
    }

    const payload = {
      locationName: selectedLocationName.value
    }

    if (syncOptions.value.regularHours) payload.regularHours = regularHours
    if (syncOptions.value.specialHours) payload.specialHours = specialHours

    await authAxios.post('/api/google-business?action=sync-hours', payload)
    showMessage('Hours synced to Google successfully!')
  } catch (error) {
    console.error('Error syncing hours:', error)
    showMessage(error.response?.data?.error || 'Failed to sync hours', 'error')
  } finally {
    syncing.value.hours = false
  }
}

// Sync services
const syncServices = async () => {
  syncing.value.services = true
  try {
    const authAxios = getAuthAxios()

    const serviceItems = [
      {
        freeFormServiceItem: {
          category: 'gcid:distillery',
          label: {
            displayName: 'Distillery Experience Tour',
            description: 'Guided journey through our artisanal rum-making process, from fresh sugarcane to the final spirit, with an exclusive behind-the-scenes look at fermentation, distillation, and barrel aging. Includes tasting.',
            languageCode: 'en'
          }
        },
        price: { currencyCode: 'VUV', units: '4000' }
      },
      {
        freeFormServiceItem: {
          category: 'gcid:distillery',
          label: {
            displayName: 'Cellar Door Tasting',
            description: 'Curated tasting of our premium rums, each sip revealing the island\'s rich terroir and craftsmanship. Approximately 30 minutes.',
            languageCode: 'en'
          }
        },
        price: { currencyCode: 'VUV', units: '2500' }
      }
    ]

    await authAxios.post('/api/google-business?action=sync-services', {
      locationName: selectedLocationName.value,
      serviceItems
    })
    showMessage('Services synced to Google successfully!')
  } catch (error) {
    console.error('Error syncing services:', error)
    showMessage(error.response?.data?.error || 'Failed to sync services', 'error')
  } finally {
    syncing.value.services = false
  }
}

// Sync food menus
const syncFoodMenus = async () => {
  syncing.value.menus = true
  try {
    const authAxios = getAuthAxios()

    // Save menu data locally first
    await saveMenuData()

    const menus = [{
      labels: [{ displayName: 'Bar Menu', languageCode: 'en' }],
      sections: menuSections.value.map(section => ({
        labels: [{ displayName: section.name, languageCode: 'en' }],
        items: section.items.map(item => {
          const menuItem = {
            labels: [{
              displayName: item.name,
              description: item.description || '',
              languageCode: 'en'
            }],
            attributes: {
              price: { currencyCode: 'VUV', units: String(item.price) }
            }
          }
          return menuItem
        })
      }))
    }]

    // Extract location ID from locationName (format: "locations/123456")
    const locationId = selectedLocationName.value.replace('locations/', '')

    await authAxios.post('/api/google-business?action=sync-food-menus', {
      accountId: selectedAccountId.value,
      locationId,
      menus
    })
    showMessage('Menu synced to Google successfully!')
  } catch (error) {
    console.error('Error syncing menus:', error)
    showMessage(error.response?.data?.error || 'Failed to sync menu', 'error')
  } finally {
    syncing.value.menus = false
  }
}

// Sync all
const syncAll = async () => {
  syncing.value.all = true
  try {
    await syncHours()
    await syncServices()
    await syncFoodMenus()
    showMessage('All data synced to Google successfully!')
  } catch (error) {
    showMessage('Some items failed to sync', 'error')
  } finally {
    syncing.value.all = false
  }
}

// Menu editor functions
const addMenuSection = () => {
  menuSections.value.push({ name: '', items: [{ name: '', price: 0, description: '' }] })
}

const removeMenuSection = (index) => {
  menuSections.value.splice(index, 1)
}

const addMenuItem = (sectionIndex) => {
  menuSections.value[sectionIndex].items.push({ name: '', price: 0, description: '' })
}

const removeMenuItem = (sectionIndex, itemIndex) => {
  menuSections.value[sectionIndex].items.splice(itemIndex, 1)
}

onMounted(() => {
  checkConnectionStatus()
})
</script>
